import type { FieldValidationRules } from "./fieldValidation";

/** Mirrors `decimal(18,6)` evaluation-criteria numeric columns in dashly-backend. */
export const EVALUATION_CRITERIA_NUMERIC_MAX = 999_999_999_999;
export const EVALUATION_CRITERIA_NUMERIC_MIN = -999_999_999_999;
export const EVALUATION_CRITERIA_NUMERIC_FRACTION_DIGITS = 6;

/** Mirrors `@Size` on backend entities — keep in sync with dashly-backend persistence entities. */

export const ALERT_FIELD_LIMITS = {
    title: { required: true, minLength: 1, maxLength: 200 },
    description: { maxLength: 1000 },
    datapoint: { maxLength: 100 },
    toleranceMode: { maxLength: 20 },
    requiredPermission: { maxLength: 100 },
    manualCriteriaValue: {
        required: true,
        min: EVALUATION_CRITERIA_NUMERIC_MIN,
        max: EVALUATION_CRITERIA_NUMERIC_MAX,
    },
    manualCriteriaNote: { maxLength: 400 },
    scopeName: { maxLength: 150 },
    recipientEmail: { required: true, minLength: 1, maxLength: 255 },
    externalEmailItem: { minLength: 1, maxLength: 255 },
    thresholdValue: {
        required: true,
        min: EVALUATION_CRITERIA_NUMERIC_MIN,
        max: EVALUATION_CRITERIA_NUMERIC_MAX,
    },
    thresholdPctChange: { required: true, min: 0, max: 100 },
    thresholdUpper: {
        required: true,
        min: EVALUATION_CRITERIA_NUMERIC_MIN,
        max: EVALUATION_CRITERIA_NUMERIC_MAX,
    },
    toleranceValue: { required: true, min: 0, max: EVALUATION_CRITERIA_NUMERIC_MAX },
    toleranceValuePercentage: { required: true, min: 0, max: 100 },
} as const satisfies Record<string, FieldValidationRules>;

export const PROCESSFLOW_FIELD_LIMITS = {
    title: { required: true, minLength: 1, maxLength: 200 },
    description: { maxLength: 1000 },
    domain: { maxLength: 100 },
    dashboardId: { maxLength: 100 },
    linkHash: { maxLength: 64 },
    requiredPermission: { maxLength: 100 },
    stepName: { required: true, minLength: 1, maxLength: 200 },
    stepDescription: { maxLength: 1000 },
    stepType: { required: true, minLength: 1, maxLength: 20 },
    completionOperatorCode: { maxLength: 20 },
    taskTitle: { required: true, minLength: 1, maxLength: 200 },
    taskDescription: { maxLength: 1000 },
    scopeFieldValue: { required: true, minLength: 1, maxLength: 150 },
    scopeValueItem: { minLength: 1, maxLength: 150 },
    scopeDataType: { maxLength: 50 },
    scopeOperator: { maxLength: 20 },
    scopeFieldName: { maxLength: 150 },
    filterFieldValue: { required: true, minLength: 1, maxLength: 250 },
    stepWeight: { min: 0, max: 100 },
    timingValue: { min: 0 },
    completionDelayValue: { min: 1 },
    tolerancePct: { min: 0, max: 100 },
    readinessPct: { min: 0, max: 100 },
    criteriaTarget: {
        required: true,
        min: EVALUATION_CRITERIA_NUMERIC_MIN,
        max: EVALUATION_CRITERIA_NUMERIC_MAX,
    },
} as const satisfies Record<string, FieldValidationRules>;

/** Mirrors `@Size` / `ObjectiveValidation` on dashly-backend objective entities. */
export const OBJECTIVE_FIELD_LIMITS = {
    title: { required: true, minLength: 1, maxLength: 200 },
    description: { maxLength: 1000 },
    domain: { maxLength: 100 },
    toleranceMode: { maxLength: 20 },
    linkHash: { maxLength: 64 },
    requiredPermission: { maxLength: 100 },
    targetLabel: { maxLength: 200 },
    toleranceValue: { min: 0 },
    targetCount: { required: true, min: 1, max: 365 },
    datapoint: { maxLength: 100 },
    functionName: { maxLength: 20 },
    sourceType: { maxLength: 50 },
    taskTitle: { required: true, minLength: 1, maxLength: 200 },
    taskDescription: { maxLength: 1000 },
} as const satisfies Record<string, FieldValidationRules>;

export const COMMENT_FIELD_LIMITS = {
    body: { required: true, minLength: 1, maxLength: 2000 },
} as const satisfies Record<string, FieldValidationRules>;

/** Mirrors `@Size` on `ActivityStreamContent.content` (project dashboard/metric conversation). */
export const ACTIVITY_STREAM_FIELD_LIMITS = {
    content: { required: true, minLength: 1, maxLength: 500 },
} as const satisfies Record<string, FieldValidationRules>;

export const CONFIDENTIAL_SHARE_FIELD_LIMITS = {
    title: { required: true, minLength: 1, maxLength: 100 },
    fieldLabel: { required: true, minLength: 1, maxLength: 100 },
    fieldType: { required: true, minLength: 1, maxLength: 50 },
    recipientEmail: { required: true, minLength: 1, maxLength: 255 },
    recipientEmailItem: { minLength: 1, maxLength: 255 },
    accessToken: { required: true, minLength: 1, maxLength: 64 },
} as const satisfies Record<string, FieldValidationRules>;

/** Mirrors `@Size` on dashly-backend project entities (Project, Dashboard, Metric, …). */
export const PROJECT_FIELD_LIMITS = {
    name: { required: true, minLength: 1, maxLength: 200 },
    description: { maxLength: 1000 },
    dashboardName: { required: true, minLength: 1, maxLength: 200 },
    goalQuestion: { maxLength: 2000 },
    targetAudience: { maxLength: 2000 },
    metricTitle: { required: true, minLength: 1, maxLength: 200 },
    shortDefinition: { maxLength: 1000 },
    sourceSystemHint: { maxLength: 500 },
    metricScopeValue: { required: true, minLength: 1, maxLength: 500 },
    stakeholderStatusComment: { maxLength: 1000 },
    modellingViewName: { maxLength: 255 },
    modellingViewNotes: { maxLength: 2000 },
    modellingModelName: { maxLength: 255 },
    modellingDisplayLabel: { maxLength: 255 },
    basicDefinition: { maxLength: 2000 },
    basicFormula: { maxLength: 2000 },
    basicScopeConditions: { maxLength: 1000 },
    technicalMappingNotes: { maxLength: 2000 },
    referenceValue: { maxLength: 100 },
    referenceSourceNote: { maxLength: 1000 },
    datasetValidationNotes: { maxLength: 2000 },
    buildabilityNotes: { maxLength: 2000 },
    hireRequestMessage: { maxLength: 2000 },
    askHelpMessage: { maxLength: 500 },
    validationFeedbackMessage: { maxLength: 4000 },
    // Fase 0 — Proces & gap-analyse.
    departmentName: { required: true, minLength: 1, maxLength: 200 },
    departmentOwnerRole: { maxLength: 200 },
    departmentDescription: { maxLength: 1024 },
    processName: { required: true, minLength: 1, maxLength: 200 },
    processShortDescription: { maxLength: 4096 },
    processStepDescription: { required: true, minLength: 1, maxLength: 500 },
    gapName: { required: true, minLength: 1, maxLength: 200 },
    gapPainPoint: { maxLength: 4096 },
    gapImpact: { maxLength: 500 },
    gapOwnerName: { maxLength: 200 },
    gapOwnerEmail: { maxLength: 200 },
} as const satisfies Record<string, FieldValidationRules>;
